import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { getFlujosEntradaHistorial } from '../../services/apis';
import { flujosEditEnabled } from '../../utils/flujosEditAvailability';
import { getEntradaHistoryLabel } from '../../utils/entradaHistoryLabels';

const PAGE_SIZE = 25;

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

const readAuthor = (event) => {
  const author = event.autor || event.usuario || event.usuario_modificador;
  if (typeof author === 'string') return author;
  return author?.nombre || author?.name || author?.login || 'Usuario no disponible';
};

const readDate = (event) => {
  const value = event.fecha || event.created_at || event.fecha_modificacion;
  if (!value) return 'Fecha no disponible';

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString('es-ES');
};

const readDifferences = (event) => {
  const differences = event.diferencias;
  if (Array.isArray(differences)) {
    return differences.map((difference, index) => {
      const field = difference.campo || difference.field || difference.path;
      return {
        key: field || index,
        label: field ? getEntradaHistoryLabel(field) : `Cambio ${index + 1}`,
        before: difference.anterior ?? difference.antes ?? difference.valor_anterior ?? difference.before,
        after: difference.nuevo ?? difference.despues ?? difference.valor_nuevo ?? difference.after,
      };
    });
  }

  if (differences && typeof differences === 'object') {
    return Object.entries(differences).map(([field, value]) => ({
      key: field,
      label: getEntradaHistoryLabel(field),
      before: value?.anterior ?? value?.antes ?? value?.before,
      after: value?.nuevo ?? value?.despues ?? value?.after ?? value,
    }));
  }

  return [];
};

const EventRow = ({ event }) => {
  const type = event.tipo || event.tipo_evento || event.accion;
  const isInitialState = type === 'ESTADO_INICIAL';
  const differences = readDifferences(event);
  const before = event.estado_antes ?? event.estado_anterior ?? event.antes;
  const after = event.estado_despues ?? event.estado_nuevo ?? event.despues;
  const previousVersion = event.version_anterior ?? event.version_previa;
  const nextVersion = event.version_nueva ?? event.version;

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} sx={{ justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: 800 }}>
          {isInitialState ? 'Estado inicial del historial' : readAuthor(event)}
        </Typography>
        <Typography variant="body2" color="text.secondary">{readDate(event)}</Typography>
      </Stack>
      {isInitialState ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Este evento marca el inicio del historial; su fecha no es necesariamente la fecha original de creacion.
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {event.motivo_cambio || event.motivo || 'Sin motivo disponible'}
        </Typography>
      )}
      {(previousVersion != null || nextVersion != null) && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Version {displayValue(previousVersion)} -&gt; {displayValue(nextVersion)}
        </Typography>
      )}
      {differences.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1.25 }}>
          {differences.map((difference) => (
            <Typography key={difference.key} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              <Box component="span" sx={{ fontWeight: 700 }}>{difference.label}: </Box>
              {displayValue(difference.before)} -&gt; {displayValue(difference.after)}
            </Typography>
          ))}
        </Stack>
      )}
      {(before != null || after != null) && (
        <Accordion disableGutters elevation={0} sx={{ mt: 1.25, border: '1px solid', borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>Ver estados completos</AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>Antes</Typography>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 12 }}>
              {JSON.stringify(before ?? null, null, 2)}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>Despues</Typography>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 12 }}>
              {JSON.stringify(after ?? null, null, 2)}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export const EntradaHistorial = ({ entradaId }) => {
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ rows: [], meta: {}, loading: flujosEditEnabled, error: '' });

  useEffect(() => {
    if (!flujosEditEnabled || !entradaId) return undefined;

    let active = true;
    setState((current) => ({ ...current, loading: true, error: '' }));
    getFlujosEntradaHistorial(entradaId, { page, limit: PAGE_SIZE })
      .then((response) => {
        if (active) {
          setState({
            rows: Array.isArray(response?.data) ? response.data : [],
            meta: response?.meta || {},
            loading: false,
            error: '',
          });
        }
      })
      .catch((error) => {
        if (active) {
          setState({
            rows: [],
            meta: {},
            loading: false,
            error: error?.response?.data?.message || 'No se ha podido cargar el historial.',
          });
        }
      });

    return () => { active = false; };
  }, [entradaId, page]);

  const totalPages = Number(state.meta.totalPages ?? state.meta.total_pages);
  const hasNext = totalPages > 0 ? page < totalPages : state.rows.length === PAGE_SIZE;

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5, color: 'primary.main' }}>
        <HistoryRoundedIcon />
        <Typography variant="h3">Historial de cambios</Typography>
      </Stack>
      {!flujosEditEnabled ? (
        <Alert severity="info" variant="outlined">
          El historial estara disponible cuando se despliegue y pruebe la migracion de auditoria.
        </Alert>
      ) : state.loading ? (
        <Typography variant="body2" color="text.secondary">Cargando historial...</Typography>
      ) : state.error ? (
        <Alert severity="error" variant="outlined">{state.error}</Alert>
      ) : state.rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No hay eventos de historial.</Typography>
      ) : (
        <>
          <Stack divider={<Divider flexItem />}>
            {state.rows.map((event, index) => (
              <EventRow key={event.id ?? `${page}-${index}`} event={event} />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', alignItems: 'center', mt: 2 }}>
            <Button size="small" color="secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>
              Anterior
            </Button>
            <Typography variant="body2">Pagina {page}</Typography>
            <Button size="small" color="secondary" disabled={!hasNext} onClick={() => setPage(page + 1)}>
              Siguiente
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
};
